import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { Typography } from '@mui/material';
import { Timeline } from '@mui/lab';
import { fetchContactActivity } from 'store/actions/activity';
import { contactSelectors } from 'store/selectors/contacts';
import { Backdrop, BreadcrumbsNavigation, Container } from 'common/components';
import TopCard from 'common/containers/TopCard';
import { CardWrapper } from 'globalStyles';
import { fetchChildrenLists, fetchContact, fetchRootList } from '../../../store/actions/contacts';
import { activitySelectors } from '../../../store/selectors/activity';

import ActivityItem from '../components/ActivityItem';

const ContactActivity = () => {
  const dispatch = useDispatch();
  const { contactUuid, listUuid, organizationUuid } = useParams();
  const { childrenLists, contactDetails, rootList } = useSelector(state => ({
    childrenLists: contactSelectors.getChildrenLists(state)[organizationUuid] || [],
    contactDetails: contactSelectors.getContactDetails(state),
    rootList: contactSelectors.getOrganizationRootList(state)[organizationUuid] || {},
  }));
  const { allActivities, hasFetchedActivities, isActivitiesFetching } = useSelector(state => ({
    allActivities: activitySelectors.getAllActivitiesListData(state),
    hasFetchedActivities: activitySelectors.hasFetchedActivities(state),
    isActivitiesFetching: activitySelectors.isActivitiesFetching(state),
  }));

  const { firstName, lastName } = contactDetails.uuid === contactUuid ? contactDetails : {};

  const contactName = `${firstName || ''} ${lastName || ''}`.trim() || 'Contact';

  const isLoadingActivities = isActivitiesFetching || !hasFetchedActivities;

  const [currentList] = childrenLists.filter(({ uuid }) => uuid === listUuid);

  const listName = rootList.uuid === listUuid ? rootList.name : currentList?.name;

  const breadcrumbs = [
    {
      link: `/${organizationUuid}/contacts/lists`,
      name: 'Lists',
    },
    {
      link: `/${organizationUuid}/contacts/lists/${listUuid}`,
      name: listName,
    },
    {
      link: `/${organizationUuid}/contacts/lists/${listUuid}/contact/${contactUuid}`,
      name: contactName,
    },
    {
      link: '',
      name: 'Activity',
    },
  ];

  useEffect(() => {
    if (Object.keys(rootList).length === 0) {
      dispatch(fetchRootList(organizationUuid)).then(data => {
        if (childrenLists.length === 0) {
          dispatch(fetchChildrenLists(data.uuid, organizationUuid));
        }
      });
    }

    dispatch(fetchContactActivity(organizationUuid, contactUuid));
    dispatch(fetchContact(listUuid, contactUuid));
  }, []);

  return (
    <>
      <BreadcrumbsNavigation isLoading={isActivitiesFetching} links={breadcrumbs} />
      <Container marginBottom="40">
        <TopCard
          title={`${contactName}'s Activity`}
          backButtonLink={`/${organizationUuid}/contacts/lists/${listUuid}/contact/${contactUuid}`}
        />
        <CardWrapper>
          {isLoadingActivities && <Typography>Loading activity...</Typography>}
          {!isLoadingActivities && !allActivities?.length && (
            <Typography>No activities found!</Typography>
          )}
          {!isLoadingActivities && !!allActivities?.length && (
            <Timeline sx={{ maxWidth: '800px' }}>
              {allActivities.map(({ label, category, action, date }) => (
                <ActivityItem
                  key={`${category}-${action}-${date}`}
                  action={action}
                  category={category}
                  date={date}
                  label={label}
                />
              ))}
            </Timeline>
          )}
        </CardWrapper>
      </Container>
      <Backdrop isOpen={false} />
    </>
  );
};

export default ContactActivity;
